import { supabase } from "~/src/integrations/supabase/client";
import { debugLog } from "~/src/lib/debug";

export interface ErrorLogData {
  errorId: string;
  errorMessage: string;
  userId?: string;
  sessionId?: string;
  errorStack?: string;
  componentName?: string;
  pageUrl?: string;
  userAgent?: string;
  errorType?: 'javascript_error' | 'network_error' | 'validation_error' | 'auth_error' | 'other';
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export class ErrorLogger {
  private static instance: ErrorLogger;
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateErrorId(): string {
    return `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public async logError(error: Error, additionalData: Partial<ErrorLogData> = {}): Promise<string> {
    const errorId = additionalData.errorId || this.generateErrorId();
    
    const errorData: ErrorLogData = {
      errorId,
      errorMessage: error.message,
      userId: additionalData.userId,
      sessionId: this.sessionId,
      errorStack: error.stack,
      componentName: additionalData.componentName,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      errorType: additionalData.errorType || 'javascript_error',
      severity: additionalData.severity || 'medium',
      ...additionalData
    };

    try {
      const { data, error: logError } = await supabase.rpc('log_error', {
        p_error_id: errorData.errorId,
        p_error_message: errorData.errorMessage,
        p_user_id: errorData.userId || null,
        p_session_id: errorData.sessionId || null,
        p_error_stack: errorData.errorStack || null,
        p_component_name: errorData.componentName || null,
        p_page_url: errorData.pageUrl || null,
        p_user_agent: errorData.userAgent || null,
        p_error_type: errorData.errorType || 'javascript_error',
        p_severity: errorData.severity || 'medium'
      });

      if (logError) {
        console.error('Failed to log error to database:', logError);
        // Fallback to console logging
        console.error('Error details:', errorData);
      } else {
        debugLog('Error logged successfully with ID:', errorId);
      }

      return errorId;
    } catch (err) {
      console.error('Error logging failed:', err);
      console.error('Original error details:', errorData);
      return errorId;
    }
  }

  public async logCustomError(
    message: string, 
    additionalData: Partial<ErrorLogData> = {}
  ): Promise<string> {
    const error = new Error(message);
    return this.logError(error, additionalData);
  }

  public async logNetworkError(
    url: string, 
    status: number, 
    message: string,
    additionalData: Partial<ErrorLogData> = {}
  ): Promise<string> {
    const error = new Error(`Network error: ${status} - ${message}`);
    return this.logError(error, {
      ...additionalData,
      errorType: 'network_error',
      componentName: additionalData.componentName || 'NetworkRequest',
      severity: status >= 500 ? 'high' : 'medium'
    });
  }

  public async logValidationError(
    field: string, 
    message: string,
    additionalData: Partial<ErrorLogData> = {}
  ): Promise<string> {
    const error = new Error(`Validation error in ${field}: ${message}`);
    return this.logError(error, {
      ...additionalData,
      errorType: 'validation_error',
      severity: 'low'
    });
  }

  public async logAuthError(
    message: string,
    additionalData: Partial<ErrorLogData> = {}
  ): Promise<string> {
    const error = new Error(`Auth error: ${message}`);
    return this.logError(error, {
      ...additionalData,
      errorType: 'auth_error',
      severity: 'high'
    });
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();

// Convenience functions
export const logError = (error: Error, additionalData?: Partial<ErrorLogData>) => 
  errorLogger.logError(error, additionalData);

export const logCustomError = (message: string, additionalData?: Partial<ErrorLogData>) => 
  errorLogger.logCustomError(message, additionalData);

export const logNetworkError = (url: string, status: number, message: string, additionalData?: Partial<ErrorLogData>) => 
  errorLogger.logNetworkError(url, status, message, additionalData);

export const logValidationError = (field: string, message: string, additionalData?: Partial<ErrorLogData>) => 
  errorLogger.logValidationError(field, message, additionalData);

export const logAuthError = (message: string, additionalData?: Partial<ErrorLogData>) => 
  errorLogger.logAuthError(message, additionalData);
